import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPages.css';

const Terms = () => {
  return (
    <div className="legal-page">
      <section className="legal-header">
        <div className="legal-header-content">
          <h1>Terms & Conditions</h1>
          <p className="subtitle">Terms & Conditions – DocuAIHub</p>
          <p className="entity-info">
            Entity: AMB GLOBAL DIGITAL SOLUTIONS LTD | Product: DocuAIHub | 
            Email: help@docuaihub.com | Customer Care: +35777788373 | 
            Address: 1 Maiou 3, 2610 Agios Epifanios Oreinis, Cyprus | 
            Website: www.ambglobaldigital.com
          </p>
        </div>
      </section>

      <section className="legal-content">
        <div className="content-wrapper">
          <aside className="table-of-contents">
            <h3>Table of Contents</h3>
            <ul>
              <li><a href="#acceptance">Acceptance of Terms</a></li>
              <li><a href="#service-overview">Service Overview</a></li>
              <li><a href="#plans-pricing">Plans and Pricing</a></li>
              <li><a href="#billing-payment">Billing and Payment</a></li>
              <li><a href="#cancellation">Cancellation & EU Consumer Rights</a></li>
              <li><a href="#account-use">Account Use & Responsibility</a></li>
              <li><a href="#intellectual-property">Intellectual Property</a></li>
              <li><a href="#limitation-liability">Limitation of Liability</a></li>
            </ul>
          </aside>

          <main className="main-content">
            <section id="acceptance" className="content-section">
              <h2>Acceptance of Terms</h2>
              <p>
                By accessing or using DocuAIHub ("the Service"), you agree to be bound by these Terms 
                and Conditions. If you do not agree to these terms, please do not use our service.
              </p>
              <p>
                These Terms constitute a legally binding agreement between you and AMB GLOBAL DIGITAL 
                SOLUTIONS LTD. We reserve the right to modify these terms at any time, and your 
                continued use of the Service constitutes acceptance of any changes.
              </p>
            </section>

            <section id="service-overview" className="content-section">
              <h2>Service Overview</h2>
              <p>
                DocuAIHub provides AI-powered document management tools including but not limited to:
              </p>
              <ul>
                <li>Document conversion (PDF to Word, Excel, PowerPoint, etc.)</li>
                <li>AI-powered document analysis, summarization, and translation</li>
                <li>Digital signature and document signing capabilities</li>
                <li>Document editing, annotation, and form creation tools</li>
                <li>Document compression, merging, and splitting</li>
                <li>OCR (Optical Character Recognition) services</li>
              </ul>
              <p>
                The Service is provided on a subscription basis with various pricing tiers. Features 
                and limitations vary by subscription level.
              </p>
            </section>

            <section id="plans-pricing" className="content-section">
              <h2>Plans and Pricing</h2>
              <p>
                DocuAIHub offers multiple subscription plans to meet different user needs. Current 
                plans and pricing are available on our pricing page. All prices are in USD unless 
                otherwise specified.
              </p>
              <p>
                <strong>Subscription Types:</strong>
              </p>
              <ul>
                <li>Free Plan: Limited features with usage restrictions</li>
                <li>Pro Plan: Full access to all features with higher usage limits</li>
                <li>Enterprise Plan: Custom solutions for organizations</li>
              </ul>
              <p>
                We reserve the right to modify our pricing at any time. Existing subscribers will be 
                notified of any price changes at least 30 days in advance.
              </p>
            </section>

            <section id="billing-payment" className="content-section">
              <h2>Billing and Payment</h2>
              <p>
                Subscription fees are billed in advance on a monthly or annual basis, depending on 
                your chosen plan. Payment is processed through secure third-party payment processors.
              </p>
              <p>
                <strong>Automatic Renewal:</strong> Your subscription will automatically renew at the 
                end of each billing period unless you cancel before the renewal date.
              </p>
              <p>
                <strong>Payment Methods:</strong> We accept major credit cards, debit cards, and other 
                payment methods as displayed during checkout.
              </p>
              <p>
                <strong>Failed Payments:</strong> If a payment fails, we will attempt to process the 
                payment again. Continued payment failures may result in service suspension.
              </p>
            </section>

            <section id="cancellation" className="content-section">
              <h2>Cancellation & EU Consumer Rights</h2>
              <p>
                <strong>Cancellation Policy:</strong> You may cancel your subscription at any time 
                through your account settings or by contacting customer support. Cancellations take 
                effect at the end of the current billing period.
              </p>
              <p>
                <strong>14-Day Right of Withdrawal (EU Customers):</strong> In accordance with EU 
                Directive 2011/83/EU, European Union consumers have the right to cancel their purchase 
                within 14 days without giving any reason. See our <Link to="/refund">Refund Policy</Link> for details.
              </p>
              <p>
                <strong>No Refunds After Use:</strong> By using the Service after purchase, you 
                acknowledge that the performance of the service has begun and you waive your right 
                to cancellation for services already rendered.
              </p>
            </section>

            <section id="account-use" className="content-section">
              <h2>Account Use & Responsibility</h2>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials 
                and for all activities that occur under your account.
              </p>
              <p><strong>Prohibited Uses:</strong></p>
              <ul>
                <li>Violating any applicable laws or regulations</li>
                <li>Infringing on intellectual property rights of others</li>
                <li>Uploading malicious code or attempting to compromise system security</li>
                <li>Using the Service to process illegal content</li>
                <li>Sharing account access with unauthorized users</li>
                <li>Automated scraping or excessive API usage</li>
              </ul>
              <p>
                We reserve the right to suspend or terminate accounts that violate these terms.
              </p>
            </section>

            <section id="intellectual-property" className="content-section">
              <h2>Intellectual Property</h2>
              <p>
                The Service, including all content, features, and functionality, is owned by AMB 
                GLOBAL DIGITAL SOLUTIONS LTD and is protected by international copyright, trademark, 
                and other intellectual property laws.
              </p>
              <p>
                <strong>Your Content:</strong> You retain all rights to documents you upload and 
                process through our Service. By uploading content, you grant us a limited license 
                to process, store, and display your documents solely for the purpose of providing 
                the Service.
              </p>
              <p>
                <strong>Document Deletion:</strong> We automatically delete uploaded documents within 
                24 hours of processing to protect your privacy.
              </p>
            </section>

            <section id="limitation-liability" className="content-section">
              <h2>Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, AMB GLOBAL DIGITAL SOLUTIONS LTD SHALL NOT 
                BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, 
                OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY 
                LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
              </p>
              <p>
                The Service is provided "AS IS" without warranties of any kind, either express or 
                implied. We do not warrant that the Service will be uninterrupted, secure, or error-free.
              </p>
              <p>
                Our total liability for any claims arising from your use of the Service is limited 
                to the amount you paid for the Service in the 12 months preceding the claim.
              </p>
            </section>

            <section className="content-section">
              <h2>Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of Cyprus. 
                Any disputes arising from these Terms or your use of the Service shall be subject to 
                the exclusive jurisdiction of the courts of Cyprus.
              </p>
            </section>

            <section className="content-section">
              <h2>Contact Information</h2>
              <p>
                For questions about these Terms and Conditions, please contact us:
              </p>
              <ul>
                <li><strong>Company:</strong> AMB GLOBAL DIGITAL SOLUTIONS LTD</li>
                <li><strong>Email:</strong> <a href="mailto:help@docuaihub.com">help@docuaihub.com</a></li>
                <li><strong>Phone:</strong> +35777788373</li>
                <li><strong>Address:</strong> 1 Maiou 3, 2610 Agios Epifanios Oreinis, Cyprus</li>
                <li><strong>Website:</strong> <a href="https://www.ambglobaldigital.com" target="_blank" rel="noopener noreferrer">www.ambglobaldigital.com</a></li>
              </ul>
            </section>

            <section className="content-section">
              <p className="last-updated">Last Updated: February 2026</p>
            </section>
          </main>
        </div>
      </section>
    </div>
  );
};

export default Terms;
